import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { ArrowLeft, ArrowRight, Check, ChevronDown, User } from 'lucide-react';
import SubmissionConfirmDialog from './SubmissionConfirmDialog';

interface RevieweeInfo {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  role: string;
  upForReview?: boolean;
}

interface Question {
  id: string;
  type: string;
  title: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  options?: Array<{
    id: string;
    label: string;
    value: string;
  }>;
  order: number;
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
}

interface PeerReviewTemplate {
  id: string;
  name: string;
  title: string;
  description?: string;
  sections: Section[];
  settings: {
    allowAnonymous: boolean;
    forceAnonymous: boolean;
    requireAllQuestions: boolean;
    showProgressBar: boolean;
    thankYouMessage?: string;
  };
}

interface FormData {
  [questionId: string]: any;
}

const PeerReviewForm: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  // Get anonymity preference from navigation state
  const anonymityFromState = location.state?.isAnonymous;
  
  const [reviewee, setReviewee] = useState<RevieweeInfo | null>(null);
  const [template, setTemplate] = useState<PeerReviewTemplate | null>(null);
  const [usersForReview, setUsersForReview] = useState<RevieweeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [hasCompletedReview, setHasCompletedReview] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Form state
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<FormData>({});
  const [submitting, setSubmitting] = useState(false);

  // Get all questions in order across sections
  const allQuestions = template?.sections
    .sort((a, b) => a.order - b.order)
    .flatMap(section => 
      section.questions
        .sort((a, b) => a.order - b.order)
        .map(question => ({ ...question, sectionTitle: section.title }))
    ) || [];

  const currentQuestion = allQuestions[currentStep];
  const totalSteps = allQuestions.length;
  const progress = totalSteps > 0 ? ((currentStep + 1) / totalSteps) * 100 : 0;

  useEffect(() => {
    fetchData();
  }, [userId]);

  const checkIfReviewCompleted = async () => {
    try {
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Check if current user has already submitted a review for this reviewee
      const reviewsRef = collection(db, 'reviewSubmissions');
      const completedQuery = query(
        reviewsRef, 
        where('reviewerId', '==', currentUser?.uid),
        where('revieweeId', '==', userId),
        where('status', '==', 'completed')
      );
      
      const completedSnapshot = await getDocs(completedQuery);
      return !completedSnapshot.empty;
    } catch (err) {
      console.error('Error checking review completion:', err);
      return false;
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const { collection, doc, getDoc, getDocs, query, where } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Check if review is already completed
      const reviewCompleted = await checkIfReviewCompleted();
      if (reviewCompleted) {
        setHasCompletedReview(true);
        setError('You have already submitted a review for this person. You cannot submit multiple reviews for the same individual.');
        return;
      }
      
      // Fetch reviewee information
      if (userId) {
        // Prevent users from reviewing themselves
        if (userId === currentUser?.uid) {
          setError('You cannot review yourself. Please select a different team member to review.');
          return;
        }

        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
          setError('User not found');
          return;
        }
        
        const userData = userDoc.data();
        setReviewee({
          id: userDoc.id,
          displayName: userData.displayName || userData.email?.split('@')[0] || 'Unknown',
          email: userData.email || '',
          photoURL: userData.photoURL,
          role: userData.role || 'employee',
          upForReview: userData.upForReview || false
        });
      }
      
      // Fetch all users up for review for dropdown
      const usersRef = collection(db, 'users');
      const reviewQuery = query(usersRef, where('upForReview', '==', true));
      const usersSnapshot = await getDocs(reviewQuery);
      
      const reviewUsers: RevieweeInfo[] = usersSnapshot.docs
        .filter(doc => doc.id !== currentUser?.uid) // Prevent users from reviewing themselves
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            displayName: data.displayName || data.email?.split('@')[0] || 'Unknown',
            email: data.email || '',
            photoURL: data.photoURL,
            role: data.role || 'employee',
            upForReview: true
          };
        }).sort((a, b) => a.displayName.localeCompare(b.displayName));
      
      setUsersForReview(reviewUsers);
      
      // Fetch active peer review template
      const templatesRef = collection(db, 'peerReviewTemplates');
      const activeTemplateQuery = query(templatesRef, where('isActive', '==', true));
      const templateSnapshot = await getDocs(activeTemplateQuery);
      
      if (templateSnapshot.empty) {
        setError('No active peer review template found. Please contact an administrator.');
        return;
      }
      
      const templateData = templateSnapshot.docs[0].data();
      setTemplate({
        id: templateSnapshot.docs[0].id,
        ...templateData
      } as PeerReviewTemplate);
      
      // Set anonymity based on template settings or navigation state
      if (templateData.settings?.forceAnonymous) {
        setIsAnonymous(true);
      } else if (!templateData.settings?.allowAnonymous) {
        setIsAnonymous(false);
      } else if (anonymityFromState !== undefined) {
        setIsAnonymous(anonymityFromState);
      } else {
        // Default to false if no preference provided
        setIsAnonymous(false);
      }
      
    } catch (err: any) {
      console.error('Error fetching review data:', err);
      setError('Failed to load review information');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (questionId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateCurrentQuestion = (): boolean => {
    if (!currentQuestion) return true;
    
    const value = formData[currentQuestion.id];
    
    if (currentQuestion.required) {
      if (value === undefined || value === null || value === '') {
        return false;
      }
      
      // For arrays (checkboxes), check if at least one item is selected
      if (Array.isArray(value) && value.length === 0) {
        return false;
      }
    }
    
    return true;
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Show confirmation dialog instead of submitting directly
      setShowConfirmDialog(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Create the review submission
      const reviewSubmission = {
        revieweeId: reviewee?.id,
        revieweeName: reviewee?.displayName,
        revieweeEmail: reviewee?.email,
        reviewerId: currentUser?.uid,
        reviewerName: isAnonymous ? null : currentUser?.displayName,
        reviewerEmail: isAnonymous ? null : currentUser?.email,
        isAnonymous,
        responses: formData,
        templateId: template?.id,
        templateName: template?.name,
        submittedAt: serverTimestamp(),
        status: 'completed'
      };
      
      console.log('Submitting review:', reviewSubmission);
      
      // Save to Firestore
      const reviewsRef = collection(db, 'reviewSubmissions');
      await addDoc(reviewsRef, reviewSubmission);
      
      // Show success message and navigate back
      alert('Review submitted successfully! Thank you for your feedback.');
      setShowConfirmDialog(false);
      navigate('/dashboard');
      
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSubmission = () => {
    handleSubmit();
  };

  const switchReviewee = (newUserId: string) => {
    if (newUserId !== userId) {
      // Reset form state when switching users
      setCurrentStep(0);
      setFormData({});
      navigate(`/review/${newUserId}`);
    }
    setShowTeamDropdown(false);
  };

  const renderQuestion = (question: Question) => {
    const value = formData[question.id];

    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <Input
            type={question.type}
            value={value || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            className="w-full"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={4}
            className="w-full px-3 py-2 border border-input rounded-md bg-background resize-none"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            min={question.min}
            max={question.max}
            className="w-full"
          />
        );

      case 'radio':
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <label key={option.id} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleInputChange(question.id, e.target.value)}
                  className="w-4 h-4 text-primary border-input focus:ring-primary"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-3">
            {question.options?.map((option) => (
              <label key={option.id} className="flex items-center space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={Array.isArray(value) && value.includes(option.value)}
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    if (e.target.checked) {
                      handleInputChange(question.id, [...currentValues, option.value]);
                    } else {
                      handleInputChange(question.id, currentValues.filter(v => v !== option.value));
                    }
                  }}
                  className="w-4 h-4 text-primary border-input rounded focus:ring-primary"
                />
                <span className="text-sm">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-md bg-background"
          >
            <option value="">Select an option...</option>
            {question.options?.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'rating':
        const min = question.min || 1;
        const max = question.max || 100;
        const currentValue = value || 50;
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Never</span>
              <span>Always</span>
            </div>
            <div className="px-2">
              <input
                type="range"
                min={min}
                max={max}
                step={1}
                value={currentValue}
                onChange={(e) => handleInputChange(question.id, parseInt(e.target.value))}
                required={question.required}
                className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer slider"
              />
            </div>
          </div>
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => handleInputChange(question.id, e.target.value)}
            className="w-full"
          />
        );

      default:
        return (
          <div className="text-muted-foreground">
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading review...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <div className={`mb-4 ${hasCompletedReview ? 'text-green-600' : 'text-destructive'}`}>
              {hasCompletedReview ? (
                <Check className="h-16 w-16 mx-auto mb-4" />
              ) : (
                <User className="h-16 w-16 mx-auto mb-4 opacity-50" />
              )}
            </div>
            <h3 className="text-lg font-medium mb-2">
              {hasCompletedReview ? 'Review Already Completed' : 'Unable to Load Review'}
            </h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            {hasCompletedReview && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-700">
                  Thank you for your feedback! Your review has been submitted and cannot be modified.
                </p>
              </div>
            )}
            <Button onClick={() => navigate('/dashboard')} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!reviewee || !template || !currentQuestion) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="flex items-center gap-2">
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-primary">{template.title}</h1>
                <p className="text-sm text-muted-foreground">
                  {isAnonymous ? 'Anonymous feedback' : 'Named feedback'}
                </p>
              </div>
            </div>
            
            {/* Team Member Dropdown */}
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                className="flex items-center gap-2"
              >
                <User className="h-4 w-4" />
                {reviewee.displayName}
                <ChevronDown className="h-4 w-4" />
              </Button>
              
              {showTeamDropdown && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-card border rounded-lg shadow-lg z-50">
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground mb-2 px-2">Switch to review:</p>
                    <div className="max-h-60 overflow-y-auto">
                      {usersForReview.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => switchReviewee(user.id)}
                          className={`w-full text-left p-2 rounded hover:bg-muted transition-colors ${
                            user.id === reviewee.id ? 'bg-primary/10 text-primary' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            {user.photoURL ? (
                              <img 
                                src={user.photoURL} 
                                alt={user.displayName} 
                                className="w-6 h-6 rounded-full" 
                              />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs font-medium">
                                  {user.displayName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium">{user.displayName}</p>
                              <p className="text-xs text-muted-foreground">{user.role}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Progress Bar */}
          {template.settings.showProgressBar && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                <span>Question {currentStep + 1} of {totalSteps}</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                {reviewee.photoURL ? (
                  <img 
                    src={reviewee.photoURL} 
                    alt={reviewee.displayName} 
                    className="w-12 h-12 rounded-full object-cover" 
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-primary font-semibold text-lg">
                      {reviewee.displayName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <div>
                  <CardTitle className="text-lg">Reviewing: {reviewee.displayName}</CardTitle>
                  <CardDescription>{reviewee.email}</CardDescription>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Question */}
              <div>
                <h3 className="text-lg font-medium mb-2">
                  {currentQuestion.title}
                  {currentQuestion.required && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </h3>
                {currentQuestion.description && (
                  <div 
                    className="text-muted-foreground text-sm mb-4 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: currentQuestion.description }}
                  />
                )}
                
                {/* Question Input */}
                <div className="mt-4">
                  {renderQuestion(currentQuestion)}
                </div>
              </div>
              
              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Previous
                </Button>
                
                <div className="text-sm text-muted-foreground">
                  {currentStep + 1} of {totalSteps}
                </div>
                
                <Button
                  onClick={handleNext}
                  disabled={!validateCurrentQuestion() || submitting}
                  className="flex items-center gap-2"
                >
                  {currentStep === totalSteps - 1 ? (
                    <>
                      <Check className="h-4 w-4" />
                      {submitting ? 'Submitting...' : 'Submit Review'}
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Submission Confirmation Dialog */}
      <SubmissionConfirmDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        onConfirm={handleConfirmSubmission}
        revieweeName={reviewee?.displayName || 'Unknown'}
        isSubmitting={submitting}
      />
    </div>
  );
};

export default PeerReviewForm;
